import { ArenaOpsUsdtSchema } from "./arena-ops-filters.dto";
import { z } from "zod";

/**
 * O1 — the headline tiles. Every figure is the LAG delta over
 * `freeAfter + lockedAfter`; `SUM(amount)` disagrees wherever a movement's
 * declared amount is not its snapshot delta.
 */
export const ArenaOpsOverviewKpisResponseSchema = z.object({
  cashInUsdt: ArenaOpsUsdtSchema,
  cashOutUsdt: ArenaOpsUsdtSchema,
  /** ADMIN_CREDIT net of ADMIN_DEBIT, so it can read below the Money tab's gross ADMIN_CREDIT row. */
  houseInjectionUsdt: ArenaOpsUsdtSchema,
  playerHeldUsdt: ArenaOpsUsdtSchema,
  netPositionUsdt: ArenaOpsUsdtSchema,
  /**
   * PLATFORM_RAKE + CREATOR_RAKE, HOUSE-WIDE — spliced in from a second,
   * unscoped read, never taken from the cohort-scoped set.
   *
   * Rake credits `ArenaPlatformConfig.rakeRecipientHubUserId`, which may also
   * sit in `teamHubUserIds`, so a cohort-scoped rake figure can read $0.00 at
   * `cohort=real` — the specific wrong answer that looks plausible, because
   * a reader concludes Cede earned nothing from real users rather than that the
   * tile is mis-scoped. The overlap is a configuration coincidence, not an
   * invariant: two independent columns, nothing enforcing the relationship.
   */
  rakeUsdt: ArenaOpsUsdtSchema,
  /**
   * The `humans ÷ seats` share of each raked pot — the part of `rakeUsdt` that
   * came out of money a real user put in.
   *
   * Same seat-proration rule and same reads as the Money tab's P3 panel, so the
   * two tiles cannot disagree. Rake off a bot-versus-bot duel is the house
   * raking its own float, so a rake total shown without this split reads as
   * revenue when roughly half of it is an internal transfer.
   */
  realUserRakeUsdt: ArenaOpsUsdtSchema,
  /** The bot and staff seats' share of the same pots. */
  cedeFundedRakeUsdt: ArenaOpsUsdtSchema,
  /**
   * Rake whose `idempotencyKey` resolves to no proposal — private-arena rooms,
   * `CREATOR_RAKE` included. Its provenance is unknown, not house-funded, so it
   * is its own figure rather than folded into `cedeFundedRakeUsdt`.
   *
   * The three parts sum to `rakeUsdt`, which is what makes the split checkable.
   */
  unattributedRakeUsdt: ArenaOpsUsdtSchema,
  /**
   * Gross SPIN_AND_GO_SPREAD over the window — the house edge in the lottery
   * format, the second way Cede earns off a duel. House-wide for the same reason
   * rake is: it credits the same account.
   */
  spreadUsdt: ArenaOpsUsdtSchema,
  /**
   * Rake + spread. The two are disjoint by construction — a Spin & Go duel pays
   * the spread instead of a rake — so the sum double-counts nothing.
   *
   * NOT what the Money tab's revenue-per-day chart plots, which is rake alone.
   */
  revenueUsdt: ArenaOpsUsdtSchema,
  /**
   * The `humans ÷ seats` share of both, on the one seat-proration rule.
   *
   * The spread has rake's circularity problem in full: a Spin & Go seat filled by
   * a bot means the house paid its own entry, so the edge it took off that seat is
   * an internal transfer. Gross revenue shown without this split overstates what
   * Cede actually earned by roughly the bot seat share.
   */
  realUserRevenueUsdt: ArenaOpsUsdtSchema,
  /** The bot and staff seats' share of the same pots. */
  cedeFundedRevenueUsdt: ArenaOpsUsdtSchema,
  /** Revenue whose `idempotencyKey` resolves to no proposal. The three parts sum to `revenueUsdt`. */
  unattributedRevenueUsdt: ArenaOpsUsdtSchema,
  accountCount: z.number().int(),
  fundedAccountCount: z.number().int(),
});
export type ArenaOpsOverviewKpisResponse = z.infer<typeof ArenaOpsOverviewKpisResponseSchema>;
